import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { RotateCcwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button, buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useModelStrategy } from '@/modules/aiAgents/hooks/useModelStrategy';
import { useModelStrategyCatalog } from '@/modules/aiAgents/hooks/useModelStrategyCatalog';
import {
  emptyStep,
  isEmptyStep,
  MAX_STEPS_PER_CHAIN,
  MODEL_STRATEGY_CHANNEL_KEYS,
  MODEL_STRATEGY_ERROR_CODES,
  ModelStrategy,
  ModelStrategyChannelKey,
  ModelStrategyStep,
  ModelStrategyValidationError,
  PER_CHANNEL_KEYS,
  strategiesEqual,
} from '@/shared/types/modelStrategy';

import { ChainEditor } from './ChainEditor';
import { extractAliasFromError } from './extractAliasFromError';

type ModelStrategySectionProps = {
  brainId: string;
  /** Legacy single language_model_id on the brain — used for the empty-state default chain. */
  legacyLanguageModelId: string | null | undefined;
};

const CHANNEL_LABEL_KEYS: Record<Exclude<ModelStrategyChannelKey, 'default'>, string> = {
  chat: 'ai_agents.model_strategy.channels.chat',
  voice: 'ai_agents.model_strategy.channels.voice',
  email: 'ai_agents.model_strategy.channels.email',
};

/** Accordion item id for the per-channel section. */
const PER_CHANNEL_ITEM = 'per-channel';

/** Seed the editor when no strategy is persisted yet. */
const seedDraft = (legacyLanguageModelId: string | null | undefined): ModelStrategy => ({
  default: [
    legacyLanguageModelId
      ? {
          language_model_id: legacyLanguageModelId,
          language_model_alias: null,
          reasoning_effort: null,
        }
      : emptyStep(),
  ],
  chat: [],
  voice: [],
  email: [],
});

/** Drops rows the user added but never picked a model for. A chain of only empty rows is kept. */
const withoutEmptySteps = (strategy: ModelStrategy): ModelStrategy => {
  const pruned = { ...strategy };
  MODEL_STRATEGY_CHANNEL_KEYS.forEach((channel) => {
    const chain = strategy[channel] ?? [];
    const configured = chain.filter((step) => !isEmptyStep(step));
    pruned[channel] = configured.length > 0 ? configured : chain;
  });
  return pruned;
};

/** True iff any channel in the strategy has at least one step. */
const strategyHasAnySteps = (strategy: ModelStrategy) =>
  MODEL_STRATEGY_CHANNEL_KEYS.some((c) => (strategy[c]?.length ?? 0) > 0);

/**
 * What a per-channel chain inherits from the default when it has no explicit
 * override. Voice is single-step, so it only inherits the default's first step.
 */
const inheritedChain = (
  defaultChain: ModelStrategyStep[],
  channel: Exclude<ModelStrategyChannelKey, 'default'>
): ModelStrategyStep[] => {
  const cloned = defaultChain.map((step) => ({ ...step }));
  return channel === 'voice' ? cloned.slice(0, 1) : cloned;
};

export const ModelStrategySection = ({
  brainId,
  legacyLanguageModelId,
}: ModelStrategySectionProps) => {
  const { t } = useTranslation();
  const {
    strategy,
    getStatus,
    refetch,
    saveDraft,
    saveStatus,
    clearAll,
    clearStatus,
    canRead,
    canWrite,
  } = useModelStrategy(brainId);
  const { buckets } = useModelStrategyCatalog(brainId);

  // Editor state holds both the local draft and the server-derived baseline
  // it was last reconciled against. Reconciliation is driven by the effect
  // below so that RTM updates and legacy-model-id changes don't leave the
  // draft pointing at stale data.
  const [editorState, setEditorState] = useState<{
    draft: ModelStrategy;
    baseline: ModelStrategy;
  } | null>(null);
  const [accordionValue, setAccordionValue] = useState<string>('');

  const hasPersistedSteps = strategyHasAnySteps(strategy);

  // Latest editorState read inside the reconciliation effect without making it
  // a dependency (which would re-run the effect on every keystroke).
  const editorStateRef = useRef(editorState);
  editorStateRef.current = editorState;

  // Server-derived baseline. When no strategy is persisted, we synthesize one
  // from the legacy single-model field so the form isn't flagged dirty on
  // mount and so a legacy-model save reflows into the default chain.
  const incomingBaseline = useMemo<ModelStrategy | null>(() => {
    if (getStatus !== 'success') {
      return null;
    }
    return hasPersistedSteps ? strategy : seedDraft(legacyLanguageModelId);
  }, [getStatus, strategy, hasPersistedSteps, legacyLanguageModelId]);

  // Reconcile the local draft with the latest server baseline. Three cases:
  //  - first successful load → adopt baseline as both draft and baseline;
  //  - baseline unchanged → no-op (content-equality check);
  //  - baseline changed remotely → if the user has no diverging edits,
  //    silently adopt; otherwise keep the edits but refresh the baseline
  //    (so isDirty stays accurate vs. latest server state) and warn once.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!incomingBaseline) {
      return;
    }
    if (!seededRef.current) {
      seededRef.current = true;
      setEditorState({ draft: incomingBaseline, baseline: incomingBaseline });
      const hasPerChannel = PER_CHANNEL_KEYS.some((c) => (incomingBaseline[c]?.length ?? 0) > 0);
      setAccordionValue(hasPerChannel ? PER_CHANNEL_ITEM : '');
      return;
    }

    const prev = editorStateRef.current;
    if (!prev) {
      setEditorState({ draft: incomingBaseline, baseline: incomingBaseline });
      return;
    }
    if (strategiesEqual(prev.baseline, incomingBaseline)) {
      return;
    }
    // Draft already matches the new server state (typical right after a save,
    // or a remote change coinciding with the user's edits), or the user has no
    // diverging edits → silently adopt. Otherwise keep the edits, refresh the
    // baseline so isDirty stays accurate, and warn once.
    const userClean = strategiesEqual(prev.draft, prev.baseline);
    if (strategiesEqual(prev.draft, incomingBaseline) || userClean) {
      setEditorState({ draft: incomingBaseline, baseline: incomingBaseline });
      return;
    }
    toast.warning(t('ai_agents.model_strategy.remote_changed'));
    setEditorState({ draft: prev.draft, baseline: incomingBaseline });
  }, [incomingBaseline, t]);

  const draft = editorState?.draft ?? null;
  const isDirty = !!editorState && !strategiesEqual(editorState.draft, editorState.baseline);

  const isMutating = saveStatus === 'pending' || clearStatus === 'pending';

  const updateChannel = useCallback(
    (channel: ModelStrategyChannelKey, next: ModelStrategyStep[]) => {
      setEditorState((prev) =>
        prev ? { ...prev, draft: { ...prev.draft, [channel]: next } } : prev
      );
    },
    []
  );

  // Start overriding a channel: seed it from the current default (voice gets
  // the first step only) so the user edits a copy rather than an empty chain.
  const overrideChannel = useCallback((channel: Exclude<ModelStrategyChannelKey, 'default'>) => {
    setEditorState((prev) =>
      prev
        ? {
            ...prev,
            draft: { ...prev.draft, [channel]: inheritedChain(prev.draft.default, channel) },
          }
        : prev
    );
  }, []);

  // Drop a channel's override so it inherits the default again.
  const resetChannel = useCallback((channel: Exclude<ModelStrategyChannelKey, 'default'>) => {
    setEditorState((prev) => (prev ? { ...prev, draft: { ...prev.draft, [channel]: [] } } : prev));
  }, []);

  const handleSave = useCallback(async () => {
    if (!editorState || saveStatus === 'pending') {
      return;
    }
    // Prune the draft before saving, so the server response matches it and the
    // reconcile effect adopts it instead of warning about a remote change.
    const toSave = withoutEmptySteps(editorState.draft);
    setEditorState({ ...editorState, draft: toSave });
    if (strategiesEqual(toSave, editorState.baseline)) {
      return;
    }
    try {
      await saveDraft(toSave);
      toast.success(t('ai_agents.model_strategy.saved'));
    } catch (error) {
      // The fetcher merges the orchestrator's 400 body onto the thrown Error
      // via `Object.assign`, so structured fields (`code`, `channel`,
      // `position`) live alongside the human-readable `message`.
      const apiError = error as ModelStrategyValidationError | undefined;
      const code = apiError?.code;
      const fallbackMessage = apiError?.message ?? String(error);

      if (code === MODEL_STRATEGY_ERROR_CODES.chainFull) {
        toast.error(t('ai_agents.model_strategy.errors.chain_full', { 0: MAX_STEPS_PER_CHAIN }));
        return;
      }
      if (code === MODEL_STRATEGY_ERROR_CODES.bucketXor) {
        toast.error(t('ai_agents.model_strategy.errors.bucket_xor'));
        return;
      }
      if (code === MODEL_STRATEGY_ERROR_CODES.unknownAlias) {
        const alias = extractAliasFromError(fallbackMessage) ?? '';
        toast.error(t('ai_agents.model_strategy.errors.unknown_alias', { 0: alias }));
        return;
      }
      toast.error(fallbackMessage);
    }
  }, [editorState, saveDraft, saveStatus, t]);

  const handleClear = useCallback(async () => {
    try {
      await clearAll();
      const seed = seedDraft(legacyLanguageModelId);
      setEditorState({ draft: seed, baseline: seed });
      setAccordionValue('');
      toast.success(t('ai_agents.model_strategy.restored'));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      toast.error(message);
    }
  }, [clearAll, legacyLanguageModelId, t]);

  // Without read permission, the strategy + catalog queries are disabled and
  // never transition out of `pending` — render nothing instead of a permanent
  // skeleton.
  if (!canRead) {
    return null;
  }
  if (getStatus === 'error' && !draft) {
    return (
      <div
        role="alert"
        className="flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-3"
      >
        <span className="text-sm leading-5 text-destructive">
          {t('ai_agents.model_strategy.errors.load_failed')}
        </span>
        <Button type="button" variant="ghost" size="sm" onClick={() => refetch()}>
          {t('common.try_again')}
        </Button>
      </div>
    );
  }
  if (!draft) {
    return <Skeleton className="h-32 w-full" />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <ChainEditor
          value={draft.default}
          buckets={buckets}
          disabled={!canWrite}
          label={t('ai_agents.model_strategy.default_model')}
          onChange={(next) => updateChannel('default', next)}
        />
      </div>

      <Accordion type="single" collapsible value={accordionValue} onValueChange={setAccordionValue}>
        <AccordionItem value={PER_CHANNEL_ITEM} className="border-none">
          <AccordionTrigger
            disabled={!canWrite}
            className={cn(
              buttonVariants({ variant: 'ghost-neutral', size: 'sm' }),
              '-ml-2 flex-none items-center gap-1 hover:no-underline [&>svg]:translate-y-0'
            )}
          >
            {t('ai_agents.model_strategy.configure_per_channel')}
          </AccordionTrigger>
          <AccordionContent className="pt-2 pb-0">
            <div className="flex flex-col gap-4">
              {PER_CHANNEL_KEYS.map((channel, index) => {
                const overridden = (draft[channel]?.length ?? 0) > 0;
                return (
                  <div
                    key={channel}
                    className={cn(
                      'flex flex-col gap-2',
                      index > 0 && 'border-t border-border pt-4'
                    )}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex min-w-0 items-baseline gap-2">
                        <span className="text-base leading-6 font-semibold text-foreground">
                          {t(CHANNEL_LABEL_KEYS[channel])}
                        </span>
                        {!overridden && (
                          <>
                            <span aria-hidden className="text-sm leading-5 text-muted-foreground">
                              ·
                            </span>
                            <span className="text-sm leading-5 text-muted-foreground">
                              {t('ai_agents.model_strategy.uses_default')}
                            </span>
                          </>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!canWrite}
                        onClick={() =>
                          overridden ? resetChannel(channel) : overrideChannel(channel)
                        }
                      >
                        {overridden
                          ? t('ai_agents.model_strategy.reset_to_default')
                          : t('ai_agents.model_strategy.override')}
                      </Button>
                    </div>
                    {overridden && (
                      <ChainEditor
                        value={draft[channel]}
                        buckets={buckets}
                        disabled={!canWrite}
                        voice={channel === 'voice'}
                        onChange={(next) => updateChannel(channel, next)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>

      {(hasPersistedSteps || isDirty) && (
        <div className="flex items-center gap-2">
          {hasPersistedSteps && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  type="button"
                  variant="outline-neutral"
                  className="gap-1.5"
                  disabled={!canWrite || isMutating}
                >
                  <RotateCcwIcon />
                  {t('ai_agents.model_strategy.restore_default')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('ai_agents.model_strategy.restore_confirm.title')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('ai_agents.model_strategy.restore_confirm.description')}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleClear}>
                    {t('ai_agents.model_strategy.restore_default')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
          {isDirty && (
            <Button
              type="button"
              className="ml-auto"
              disabled={!canWrite || isMutating}
              onClick={handleSave}
            >
              {t('common.save')}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
